import type { MaybeRef, MaybeRefOrGetter } from 'vue';
import { toValue, onBeforeUnmount, onMounted, ref } from 'vue';
import type { SelectItem, SelectItemSeparator } from './types';

type InputSelection<T> = {
  items: MaybeRefOrGetter<Readonly<(T | SelectItemSeparator)[]>>;
  toggle?: (item: T) => void;
  listenKeyboard?: MaybeRef<boolean>;
};

/**
 * Хук для управления состоянием наведения и клавиатурной навигации в списках
 */

const useListHoverController = <T extends SelectItem>({
  items,
  toggle,
  listenKeyboard
}: InputSelection<T>) => {
  // Hover handling
  const hoveredIndex = ref<number | null>(null);
  const hoveredFromKeyboard = ref(false);

  const onHover = (index: number, fromKeyboard = false) => {
    hoveredIndex.value = index;
    hoveredFromKeyboard.value = fromKeyboard;
  };

  const isHovered = (index: number) => {
    return hoveredIndex.value === index;
  };

  // Keyboard navigation
  const findUpActiveItem = (startIndex: number) => {
    const itemsValue = toValue(items);
    const firstActiveIndex = itemsValue.findIndex((item) => !item.disabled);
    if (startIndex < firstActiveIndex) {
      return itemsValue.findLastIndex((item) => !item.disabled);
    }
    for (let i = startIndex; i >= 0; i--) {
      if (!itemsValue[i].disabled) {
        return i;
      }
    }
    return null;
  };

  const findDownActiveItem = (startIndex: number) => {
    const itemsValue = toValue(items);
    const lastActiveIndex = itemsValue.findLastIndex((item) => !item.disabled);
    if (startIndex >= lastActiveIndex + 1) {
      return itemsValue.findIndex((item) => !item.disabled);
    }
    for (let i = startIndex; i < itemsValue.length; i++) {
      if (!itemsValue[i].disabled) {
        return i;
      }
    }
    return startIndex;
  };

  const onArrowUp = () => {
    const itemsValue = toValue(items);
    const lastActiveIndex = itemsValue.findLastIndex((item) => !item.disabled);
    if (hoveredIndex.value === null) {
      onHover(lastActiveIndex > -1 ? lastActiveIndex : 0, true);
      return;
    }
    const nextIndex = findUpActiveItem(hoveredIndex.value - 1);
    if (nextIndex === null) {
      return;
    }

    onHover(nextIndex, true);
  };

  const onArrowDown = () => {
    const itemsValue = toValue(items);
    const firstActiveIndex = itemsValue.findIndex((item) => !item.disabled);
    if (hoveredIndex.value === null) {
      onHover(firstActiveIndex > -1 ? firstActiveIndex : 0, true);
      return;
    }
    const nextIndex = findDownActiveItem(hoveredIndex.value + 1);
    if (nextIndex === null) {
      return;
    }

    onHover(nextIndex, true);
  };

  const onEnter = () => {
    if (!toggle || hoveredIndex.value === null) {
      return;
    }
    const item = toValue(items)[hoveredIndex.value];
    if (!item) {
      return;
    }
    toggle(item as T);
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!toValue(listenKeyboard)) {
      return;
    }
    const hasModifiers = e.ctrlKey || e.metaKey || e.shiftKey;
    if (hasModifiers) {
      return;
    }
    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault();
        onArrowUp();
        break;
      case 'ArrowDown':
        e.preventDefault();
        onArrowDown();
        break;
      case 'Enter':
        e.preventDefault();
        onEnter();
        break;
    }
  };

  onMounted(() => {
    document.addEventListener('keydown', handleKeyDown);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('keydown', handleKeyDown);
  });

  return {
    onHover,
    isHovered,
    hoveredIndex,
    hoveredFromKeyboard
  };
};

export default useListHoverController;
